import { useState, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { applySeo } from "@/lib/seo";
import { trpc } from "@/lib/trpc";
import { AgencyLogo } from "@/components/AgencyLogo";
import { BarometerNav } from "@/components/BarometerNav";
import { BarometerHero } from "@/components/BarometerHero";
import { CrossSelling } from "@/components/CrossSelling";
import { generateDatenschutzPdf } from "@/lib/generatePdf";
import { BAROMETER_PRICES, BAROMETER_MIT_VIDEOCALL, formatPrice } from "@shared/prices";
import { toast } from "sonner";

// ─── Konstanten ───────────────────────────────────────────────────────────────
const PORTRAIT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-portrait_abbef783.jpg";
const MEETERGO_URL = "https://cal.meetergo.com/larissa-girenko/30-min-meeting-or-larissa-girenko?utm_source=ig&utm_medium=social&utm_content=link_in_bio";

const C = {
  teal: "#0f766e",
  tealDark: "#0d5e57",
  tealLight: "#ccfbf1",
  text: "#111827",
  muted: "#6b7280",
  bgGray: "#f9fafb",
};

// ─── Typen ────────────────────────────────────────────────────────────────────
interface DimDetail {
  check: string;
  status: "ok" | "fehlt" | "warnung" | "kritisch" | "info";
  punkte: number;
  hinweis?: string;
}
interface Dimension {
  dimension: string;
  punkte: number;
  max: number;
  details: DimDetail[];
}
interface Optimierung {
  dimension: string;
  check: string;
  hinweis: string;
  prioritaet: "kritisch" | "hoch" | "mittel" | "niedrig";
}
interface AnalyseResult {
  url: string;
  domain: string;
  total_score: number;
  max_score: number;
  prozent: number;
  grade: string;
  analyse_dauer: number;
  dimensionen: Dimension[];
  optimierungen: Optimierung[];
  has_ssl?: boolean;
  has_impressum?: boolean;
  has_datenschutz?: boolean;
  has_cookie_banner?: boolean;
}

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────
function gradeColor(grade: string): string {
  if (grade.startsWith("A")) return "#059669";
  if (grade === "B") return "#0284c7";
  if (grade === "C") return C.teal;
  if (grade === "D") return "#d97706";
  return "#dc2626";
}

function statusIcon(status: string): string {
  if (status === "ok") return "✓";
  if (status === "kritisch") return "✗";
  if (status === "warnung") return "⚠";
  return "–";
}

function statusColor(status: string): string {
  if (status === "ok") return "#059669";
  if (status === "kritisch") return "#dc2626";
  if (status === "warnung") return "#d97706";
  return C.muted;
}

// ─── Ergebnisseite ────────────────────────────────────────────────────────────
function DatenschutzResultsView({ data, onReset }: { data: AnalyseResult; onReset: () => void }) {
  const [activeTab, setActiveTab] = useState<"overview" | "dimensions" | "recommendations">("overview");

  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (data.prozent / 100) * circumference;

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <BarometerNav current="/datenschutz-barometer" accentColor={C.teal} />
      <div style={{ height: "4px", backgroundColor: C.teal }} />

      {/* Ergebnis-Hero */}
      <section className="bg-black px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Score-Ring */}
            <div className="flex-shrink-0">
              <svg width="140" height="140" viewBox="0 0 140 140">
                <circle cx="70" cy="70" r="54" fill="none" stroke="#1f2937" strokeWidth="12" />
                <circle cx="70" cy="70" r="54" fill="none" stroke={gradeColor(data.grade)} strokeWidth="12"
                  strokeDasharray={circumference} strokeDashoffset={offset}
                  strokeLinecap="round" transform="rotate(-90 70 70)" />
                <text x="70" y="62" textAnchor="middle" fill="white" fontSize="28" fontWeight="900" fontFamily="'Bebas Neue', sans-serif">{data.prozent}</text>
                <text x="70" y="82" textAnchor="middle" fill="#9ca3af" fontSize="13" fontFamily="'DM Sans', sans-serif">von 100</text>
              </svg>
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: C.teal }}>Datenschutz-Barometer™ Ergebnis</p>
              <h1 className="font-black uppercase text-white mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.8rem, 5vw, 3rem)" }}>{data.domain}</h1>
              <p className="text-gray-400 text-sm mb-4">{data.total_score} von {data.max_score} Punkten · Analyse in {data.analyse_dauer}s</p>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                {data.has_ssl && <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 border" style={{ color: "#059669", borderColor: "#059669" }}>SSL/HTTPS ✓</span>}
                {data.has_impressum && <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 border" style={{ color: "#059669", borderColor: "#059669" }}>Impressum ✓</span>}
                {data.has_datenschutz && <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 border" style={{ color: "#059669", borderColor: "#059669" }}>Datenschutzerklärung ✓</span>}
                {data.has_cookie_banner && <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 border" style={{ color: C.teal, borderColor: C.teal }}>Cookie-Banner erkannt</span>}
              </div>
            </div>
            <button onClick={onReset} className="text-xs font-bold uppercase tracking-widest px-6 py-3 border border-gray-600 text-gray-300 hover:border-white hover:text-white transition-colors">
              Neue Analyse
            </button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex">
          {(["overview", "dimensions", "recommendations"] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className="px-6 py-4 text-xs font-bold uppercase tracking-widest transition-colors border-b-2"
              style={{
                borderColor: activeTab === tab ? C.teal : "transparent",
                color: activeTab === tab ? C.teal : C.muted,
              }}>
              {tab === "overview" ? "Übersicht" : tab === "dimensions" ? "Dimensionen" : "Empfehlungen"}
            </button>
          ))}
        </div>
      </div>

      {/* Tab-Inhalt */}
      <div className="max-w-4xl mx-auto w-full px-6 py-10">
        {activeTab === "overview" && (
          <div className="space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {data.dimensionen.map((dim) => (
                <div key={dim.dimension} className="p-4 border border-gray-200 text-center">
                  <div className="font-black text-2xl mb-1" style={{ color: C.teal, fontFamily: "'Bebas Neue', sans-serif" }}>{dim.punkte}/{dim.max}</div>
                  <div className="text-xs text-gray-500 leading-tight">{dim.dimension}</div>
                  <div className="mt-2 h-1 bg-gray-100">
                    <div className="h-1 transition-all" style={{ width: `${(dim.punkte / dim.max) * 100}%`, backgroundColor: C.teal }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="border border-gray-200 p-6">
              <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: C.teal }}>Wichtigste Maßnahmen</p>
              <div className="space-y-3">
                {data.optimierungen.filter(o => o.prioritaet === "kritisch" || o.prioritaet === "hoch").slice(0, 5).map((opt, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-xs font-bold px-2 py-0.5 flex-shrink-0" style={{
                      backgroundColor: opt.prioritaet === "kritisch" ? "#fef2f2" : "#fffbeb",
                      color: opt.prioritaet === "kritisch" ? "#dc2626" : "#d97706",
                    }}>{opt.prioritaet.toUpperCase()}</span>
                    <p className="text-sm text-gray-700">{opt.hinweis}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "dimensions" && (
          <div className="space-y-6">
            {data.dimensionen.map((dim) => (
              <div key={dim.dimension} className="border border-gray-200">
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                  <h3 className="font-bold text-sm uppercase tracking-widest">{dim.dimension}</h3>
                  <span className="font-black text-lg" style={{ color: C.teal, fontFamily: "'Bebas Neue', sans-serif" }}>{dim.punkte}/{dim.max}</span>
                </div>
                <div className="divide-y divide-gray-50">
                  {dim.details.map((detail, i) => (
                    <div key={i} className="flex items-start gap-3 px-4 py-3">
                      <span className="font-bold text-sm flex-shrink-0 w-5 text-center" style={{ color: statusColor(detail.status) }}>{statusIcon(detail.status)}</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800">{detail.check}</p>
                        {detail.hinweis && <p className="text-xs text-gray-500 mt-0.5">{detail.hinweis}</p>}
                      </div>
                      <span className="text-xs font-bold flex-shrink-0" style={{ color: C.teal }}>{detail.punkte}P</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === "recommendations" && (
          <div className="space-y-4">
            {/* Zusammenfassung */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {(['kritisch','hoch','mittel'] as const).map(prio => {
                const count = data.optimierungen.filter(o => o.prioritaet === prio).length;
                const cfg = {
                  kritisch: { bg: '#fef2f2', color: '#dc2626', label: 'Kritisch' },
                  hoch: { bg: '#fffbeb', color: '#d97706', label: 'Hoch' },
                  mittel: { bg: '#f0fdf4', color: '#059669', label: 'Mittel' },
                }[prio];
                return (
                  <div key={prio} className="p-3 text-center border" style={{ borderColor: cfg.color + '40', backgroundColor: cfg.bg }}>
                    <div className="font-black text-2xl" style={{ color: cfg.color, fontFamily: "'Bebas Neue', sans-serif" }}>{count}</div>
                    <div className="text-xs font-bold uppercase" style={{ color: cfg.color }}>{cfg.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Empfehlungen nach Priorität gruppiert */}
            {(['kritisch','hoch','mittel','niedrig'] as const).map(prio => {
              const items = data.optimierungen.filter(o => o.prioritaet === prio);
              if (items.length === 0) return null;
              const cfg = {
                kritisch: { color: '#dc2626', label: '🚨 Kritisch – sofort handeln', bg: '#fef2f2' },
                hoch: { color: '#d97706', label: '⚠️ Hoch – zeitnah umsetzen', bg: '#fffbeb' },
                mittel: { color: '#059669', label: '✅ Mittel – empfohlen', bg: '#f0fdf4' },
                niedrig: { color: '#6b7280', label: 'ℹ️ Niedrig – optional', bg: '#f9fafb' },
              }[prio];
              return (
                <div key={prio}>
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <div className="h-px flex-1" style={{ backgroundColor: cfg.color + '30' }} />
                    <span className="text-xs font-bold uppercase tracking-widest" style={{ color: cfg.color }}>{cfg.label}</span>
                    <div className="h-px flex-1" style={{ backgroundColor: cfg.color + '30' }} />
                  </div>
                  <div className="space-y-3">
                    {items.map((opt, i) => {
                      // Hinweistext in nummerierte Schritte aufteilen (wenn (1) (2) etc. vorhanden)
                      const steps = opt.hinweis.split(/(?=\(\d+\))/).filter(s => s.trim());
                      const hasSteps = steps.length > 1;
                      return (
                        <div key={i} className="border p-4" style={{ borderColor: cfg.color + '30', backgroundColor: cfg.bg + '80' }}>
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div>
                              <h4 className="font-bold text-sm text-gray-900">{opt.check}</h4>
                              <p className="text-xs mt-0.5" style={{ color: cfg.color }}>{opt.dimension}</p>
                            </div>
                            <span className="text-xs font-bold px-2 py-0.5 flex-shrink-0 uppercase" style={{ backgroundColor: cfg.color + '15', color: cfg.color }}>{opt.prioritaet}</span>
                          </div>
                          {hasSteps ? (
                            <div className="space-y-1.5 mt-3">
                              {steps.map((step, si) => (
                                <div key={si} className="flex items-start gap-2">
                                  <span className="text-xs font-bold flex-shrink-0 mt-0.5" style={{ color: cfg.color }}>{si === 0 ? '→' : step.match(/^\(\d+\)/)?.[0] ?? '→'}</span>
                                  <p className="text-sm text-gray-700 leading-relaxed">{si === 0 ? step : step.replace(/^\(\d+\)\s*/, '')}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-700 leading-relaxed mt-2">{opt.hinweis}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CTA */}
      <section className="border-t border-gray-200 py-12 px-6 text-center">
        <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.teal }}>Rechtssicherheit herstellen</p>
        <h2 className="font-black uppercase mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}>DSGVO-Konformität umsetzen</h2>
        <p className="text-gray-500 text-sm max-w-lg mx-auto mb-6">DSGVO-Verstöße können teuer werden. Jetzt ist der richtige Zeitpunkt, den Datenschutz-Status deiner Website zu kennen und rechtliche Risiken zu minimieren.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => generateDatenschutzPdf(data as unknown as Record<string, unknown>)}
            className="text-white font-bold text-sm uppercase tracking-widest px-8 py-4 transition-colors"
            style={{ backgroundColor: '#0f766e', fontFamily: "'DM Sans', sans-serif" }}>
            📄 PDF-Report herunterladen
          </button>
          <a href="https://www.werbeagentur-larissa-girenko.de/kontakt" target="_blank" rel="noopener noreferrer"
            className="inline-block text-white font-bold text-sm tracking-widest uppercase px-10 py-4"
            style={{ backgroundColor: C.teal }}>Beratungsgespräch anfragen →</a>
        </div>
      </section>

      <footer className="border-t border-gray-200 px-6 py-8" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AgencyLogo withLogo size="sm" />
          </div>
          <div className="flex flex-wrap items-center gap-4 justify-center">
            {[{ href: "/", label: "Übersicht" }, { href: "/sensorik", label: "Sensorik" }, { href: "/lokal", label: "Lokal" }, { href: "/geo-barometer", label: "GEO" }, { href: "/seo-barometer", label: "SEO" }, { href: "/conversion-barometer", label: "Conversion" }, { href: "/accessibility-barometer", label: "Accessibility" }, { href: "/datenschutz-barometer", label: "Datenschutz" }, { href: "/impressum", label: "Impressum" }].map(({ href, label }) => (
              <a key={href} href={href} className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase">{label}</a>
            ))}
          </div>
          <p className="text-xs text-gray-400">© 2026 Werbeagentur Larissa Girenko</p>
        </div>
      </footer>
    </div>
  );
}

// ─── Ladeanimation ────────────────────────────────────────────────────────────
function LoadingScreen({ url }: { url: string }) {
  const steps = ["Seite laden…", "SSL/HTTPS prüfen…", "Impressum analysieren…", "Cookie-Banner erkennen…", "Datenschutzerklärung prüfen…", "Externe Dienste scannen…", "DSGVO-Konformität bewerten…", "Ergebnis berechnen…"];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setStep(s => Math.min(s + 1, steps.length - 1)), 4000);
    return () => clearInterval(iv);
  }, []);
  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <BarometerNav current="/datenschutz-barometer" accentColor={C.teal} />
      <div className="text-center mt-16">
        <div className="w-16 h-16 border-4 border-gray-700 rounded-full animate-spin mx-auto mb-8" style={{ borderTopColor: C.teal }} />
        <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.teal }}>Datenschutz-Barometer™ analysiert</p>
        <h2 className="font-black uppercase text-white mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}>{url}</h2>
        <p className="text-gray-400 text-sm mb-8">{steps[step]}</p>
        <div className="flex gap-2 justify-center">
          {steps.map((_, i) => (
            <div key={i} className="w-2 h-2 rounded-full transition-all" style={{ backgroundColor: i <= step ? C.teal : "#374151" }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function DatenschutzHome() {
  const { user } = useAuth();
  const isAdminUser = user?.role === "admin";
  useEffect(() => applySeo({
    title: "Datenschutz-Barometer™ – DSGVO-Analyse deiner Website | WERBEAGENTUR Larissa Girenko",
    description: "Ist deine Website DSGVO-konform? Datenschutz-Barometer™ prüft Cookie-Banner, Datenschutzerklärung, Drittanbieter & mehr. Vollanalyse ab 97 €.",
    path: "/datenschutz-barometer",
  }), []);
  const [url, setUrl] = useState("");
  const [result, setResult] = useState<AnalyseResult | null>(null);
  const [previewResult, setPreviewResult] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [analyzedUrl, setAnalyzedUrl] = useState("");

  const analyze = trpc.datenschutzBarometer.analyze.useMutation({
    onSuccess: (data) => {
      setIsLoading(false);
      if (data.success && data.data) {
        setResult(data.data as unknown as AnalyseResult);
      }
    },
    onError: () => setIsLoading(false),
  });
  const previewMutation = trpc.datenschutzBarometer.analyzePreview.useMutation({
    onSuccess: (res) => {
      setIsLoading(false);
      if (res.success && res.data) setPreviewResult(res.data as Record<string, unknown>);
    },
    onError: (err) => { setIsLoading(false); console.error(err.message); },
  });

  const handleAnalyze = () => {
    if (!url.trim()) return;
    setAnalyzedUrl(url.trim());
    setResult(null);
    setPreviewResult(null);
    setIsLoading(true);
    if (isAdminUser) {
      analyze.mutate({ url: url.trim() });
    } else {
      previewMutation.mutate({ url: url.trim() });
    }
  };
  const createCheckout = trpc.payment.createCheckout.useMutation({
    onSuccess: (data) => {
      if (data.checkoutUrl) window.open(data.checkoutUrl, "_blank");
    },
    onError: (err) => toast.error(err.message),
  });
  const handleBuy = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({
      origin: window.location.origin,
      productId: "datenschutz_analyse",
      targetUrl: (previewResult as Record<string, unknown>)?.url as string || url.trim() || undefined,
      returnPath: "/datenschutz-barometer",
    });
  };
  const handleBuyVideoCall = () => {
    toast.info("Du wirst zu Stripe weitergeleitet …");
    createCheckout.mutate({
      origin: window.location.origin,
      productId: "datenschutz_mit_videocall",
      targetUrl: (previewResult as Record<string, unknown>)?.url as string || url.trim() || undefined,
      returnPath: "/datenschutz-barometer",
    });
  };
  const handleReset = () => { setResult(null); setPreviewResult(null); setIsLoading(false); setUrl(""); analyze.reset(); previewMutation.reset(); };
  const handleBooking = () => {
    const bookingUrl = url.trim()
      ? `${MEETERGO_URL}&notes=${encodeURIComponent("Website: " + url.trim())}`
      : MEETERGO_URL;
    window.open(bookingUrl, "_blank", "noopener,noreferrer");
  };
  if (isLoading) return <LoadingScreen url={analyzedUrl} />;
  if (result) return <DatenschutzResultsView data={result} onReset={handleReset} />;

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <BarometerNav current="/datenschutz-barometer" accentColor={C.teal} />
      <div style={{ height: "4px", backgroundColor: C.teal }} />

      <BarometerHero
        name="Datenschutz-Barometer™"
        headline="DSGVO-Bußgeld bis 20 Mio. € —<br />weißt du, wo deine Website angreifbar ist?"
        subtext={`5 Dimensionen · 160+ Kriterien · DSGVO, Cookie-Banner, SSL. Kostenlose Vorschau in 60 Sekunden — persönliche Vollanalyse von Larissa ab ${formatPrice(BAROMETER_PRICES.datenschutz)}.`}
        accentColor={C.teal}
        ctaColor="#115751"
        stats={[
          { val: "5", label: "Dimensionen" },
          { val: "160+", label: "Kriterien" },
          { val: "DSGVO", label: "Rechtsrahmen" },
        ]}
        url={url}
        onUrlChange={setUrl}
        onSubmit={handleAnalyze}
        isLoading={isLoading}
        error=""
        isAdmin={isAdminUser}
        adminCtaText="Datenschutz-Vollanalyse starten →"
      />

      {/* Preview-Ergebnis: nur Scores */}
      {previewResult && (
        <section className="max-w-3xl mx-auto w-full px-6 py-12">
          <div className="border border-gray-200 p-6" style={{ backgroundColor: C.bgGray }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: C.teal }} />
              <p className="text-xs font-bold tracking-widest uppercase" style={{ color: C.teal }}>Datenschutz-Barometer™ Vorschau</p>
              <span className="text-xs text-gray-400 truncate">{url}</span>
            </div>
            {(() => {
              const score = Number(previewResult.total_score ?? 0);
              const maxScore = Number(previewResult.max_score ?? 100);
              const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
              const grade = pct >= 80 ? "A" : pct >= 65 ? "B" : pct >= 45 ? "C" : pct >= 25 ? "D" : "F";
              const gColor = pct >= 70 ? "#16a34a" : pct >= 45 ? "#ca8a04" : "#dc2626";
              return (
                <div className="flex items-center gap-6 mb-6 p-4 bg-white border border-gray-200">
                  <div className="text-center flex-shrink-0">
                    <div className="text-5xl font-black" style={{ fontFamily: "'Bebas Neue', sans-serif", color: gColor }}>{pct}</div>
                    <div className="text-xs text-gray-400">von 100</div>
                  </div>
                  <div>
                    <div className="text-lg font-black uppercase" style={{ fontFamily: "'Bebas Neue', sans-serif", color: gColor }}>Note {grade} · {pct >= 70 ? "DSGVO-konform" : pct >= 45 ? "Risikobehaftet" : "Kritisch"}</div>
                    <div className="text-sm text-gray-500 mt-1">{score} von {maxScore} Punkten</div>
                  </div>
                </div>
              );
            })()}
            {(() => {
              const dims = (previewResult.dimension_scores as Array<{ label: string; score: number; max: number }>) ?? [];
              return dims.length > 0 ? (
                <div className="space-y-2 mb-6">
                  <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.teal }}>Dimensionen auf einen Blick</p>
                  {dims.map((d) => {
                    const dp = d.max > 0 ? Math.round((d.score / d.max) * 100) : 0;
                    const dc = dp >= 70 ? "#16a34a" : dp >= 45 ? "#ca8a04" : "#dc2626";
                    return (
                      <div key={d.label} className="flex items-center gap-3">
                        <span className="text-xs w-40 flex-shrink-0">{d.label}</span>
                        <div className="flex-1 bg-gray-200 h-2"><div style={{ width: `${dp}%`, backgroundColor: dc, height: "100%" }} /></div>
                        <span className="text-xs font-bold w-8 text-right" style={{ color: dc }}>{dp}%</span>
                      </div>
                    );
                  })}
                </div>
              ) : null;
            })()}
            <div className="border-t border-gray-200 pt-5">
              <p className="text-xs font-bold tracking-widest uppercase mb-4 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Nächster Schritt — Wähle deine Option</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="border-2 border-gray-200 p-5 flex flex-col">
                  <p className="text-xs font-bold tracking-widest uppercase mb-1 text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>Option 1</p>
                  <h3 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Vollständige Analyse</h3>
                  <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Alle Befunde, priorisierte Empfehlungen und persönlicher PDF-Report. Lieferung in 72 Stunden.</p>
                  <div className="text-xl font-black mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", color: C.teal }}>{formatPrice(BAROMETER_PRICES.datenschutz)}</div>
                  <button onClick={handleBuy} className="w-full text-white font-bold text-xs uppercase tracking-widest px-4 py-3 hover:opacity-90 transition-opacity" style={{ backgroundColor: C.teal, fontFamily: "'DM Sans', sans-serif" }}>Analyse buchen</button>
                </div>
                <div className="border-2 p-5 flex flex-col relative" style={{ borderColor: "#6d0000", backgroundColor: "#6d000008" }}>
                  <div className="absolute -top-3 left-4 px-3 py-1 text-white text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Empfohlen</div>
                  <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Option 2</p>
                  <h3 className="font-black uppercase text-lg mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Analyse + VideoCall</h3>
                  <p className="text-gray-600 text-xs mb-3 flex-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>Vollständige Analyse + 30-minütige persönliche Ergebnis-Interpretation mit Larissa per VideoCall.</p>
                  <div className="text-xl font-black mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", color: "#6d0000" }}>{formatPrice(BAROMETER_MIT_VIDEOCALL)}</div>
                  <p className="text-xs text-gray-400 mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>inkl. 30-Min-VideoCall (+79 €)</p>
                  <button onClick={handleBuyVideoCall} className="w-full text-white font-bold text-xs uppercase tracking-widest px-4 py-3 hover:opacity-90 transition-opacity" style={{ backgroundColor: "#6d0000", fontFamily: "'DM Sans', sans-serif" }}>Analyse + VideoCall buchen</button>
                </div>
              </div>
              <button onClick={handleReset} className="block w-full text-center text-xs text-gray-400 mt-2 hover:text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>Andere URL analysieren</button>
            </div>
          </div>
        </section>
      )}


      {/* Dimensionen-Kacheln */}
      <section className="border-t border-gray-200 py-16 px-6" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
            {[
              { icon: "/manus-storage/29_7f3bb394.svg", label: "Rechtliche Grundlagen", desc: "Impressum, DSE, AGB, Widerruf" },
              { icon: "/manus-storage/29_7f3bb394.svg", label: "Cookie-Management", desc: "Banner, Opt-in, Kategorien" },
              { icon: "/manus-storage/29_7f3bb394.svg", label: "Datensicherheit", desc: "SSL, HTTPS, Mixed Content" },
              { icon: "/manus-storage/29_7f3bb394.svg", label: "Transparenz & Rechte", desc: "Kontakt, Zwecke, Betroffenenrechte" },
              { icon: "/manus-storage/32_04885248.svg", label: "Tracking & Dienste", desc: "Fonts, Analytics, Pixel, Maps, YouTube" },
            ].map((f) => (
              <div key={f.label} className="border p-4 text-center bg-white">
                <img src={f.icon} alt="" className="w-7 h-7 mb-2 object-contain" style={{ opacity: 0.85 }} />
                <div className="text-xs font-bold uppercase tracking-widest mb-1">{f.label}</div>
                <div className="text-xs" style={{ color: C.muted }}>{f.desc}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.teal }}>Warum DSGVO-Konformität entscheidend ist</p>
            <h3 className="font-black uppercase leading-none mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.5rem, 4vw, 2.5rem)" }}>Bis zu 20 Mio. €<br />Bußgeld bei Verstößen</h3>
            <p className="text-gray-500 text-sm max-w-2xl mx-auto">Die DSGVO gilt seit 2018 – doch viele Websites sind noch immer nicht konform. Abmahnungen, Bußgelder und Reputationsschäden sind die Folge. Der Datenschutz-Barometer zeigt dir, wo deine Website steht.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              { number: "01", title: "URL eingeben", desc: "Gib deine Website-URL ein – keine Anmeldung erforderlich." },
              { number: "02", title: "DSGVO-Analyse", desc: "160+ Datenschutz-Kriterien werden automatisch in unter 60 Sekunden geprüft." },
              { number: "03", title: "Bericht erhalten", desc: "Du bekommst einen detaillierten Report mit priorisierten Handlungsempfehlungen." },
            ].map((v) => (
              <div key={v.number} className="p-6 border" style={{ borderColor: "#e5e7eb" }}>
                <span className="font-black text-4xl" style={{ color: C.teal, fontFamily: "'Bebas Neue', sans-serif" }}>{v.number}</span>
                <h4 className="font-bold text-sm uppercase tracking-widest mt-2 mb-2">{v.title}</h4>
                <p className="text-xs text-gray-500">{v.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { stat: "€20M", label: "Maximales Bußgeld", sub: "oder 4 % des weltweiten Jahresumsatzes (DSGVO Art. 83)" },
              { stat: "95%", label: "der Websites", sub: "haben DSGVO-Mängel (Studie Universität Hamburg 2023)" },
              { stat: "3x", label: "mehr Abmahnungen", sub: "seit DSGVO-Einführung 2018 (Bitkom 2024)" },
            ].map((s) => (
              <div key={s.stat} className="p-6 text-center border" style={{ borderColor: "#e5e7eb" }}>
                <div className="font-black text-4xl mb-2" style={{ color: C.teal, fontFamily: "'Bebas Neue', sans-serif" }}>{s.stat}</div>
                <div className="text-sm mb-1">{s.label}</div>
                <div className="text-xs" style={{ color: C.muted }}>{s.sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 border-l-4 pl-6 py-2 max-w-2xl mx-auto" style={{ borderColor: C.teal }}>
            <p className="text-sm italic text-gray-600 mb-2">"Datenschutz ist kein bürokratisches Hindernis – er ist ein Wettbewerbsvorteil. Kunden vertrauen Unternehmen, die transparent mit ihren Daten umgehen. Der Datenschutz-Barometer macht messbar, wie gut deine Website dieses Vertrauen verdient."</p>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: C.teal }}>– Larissa Girenko</p>
          </div>

          <div className="mt-12 text-center">
            <button onClick={handleBooking}
              className="text-white font-bold text-sm tracking-widest uppercase px-10 py-4 transition-opacity hover:opacity-90"
              style={{ backgroundColor: "#115751" }}
            >Jetzt kostenlose Erstberatung buchen</button>
          </div>
        </div>
      </section>

      {/* Larissa-Sektion */}
      <section className="border-t border-gray-200 py-16 px-6 bg-black">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="relative overflow-hidden" style={{ maxHeight: "480px" }}>
                <img src={PORTRAIT_IMG} alt="Larissa Girenko – Datenschutz-Expertin" className="w-full object-cover object-top" style={{ maxHeight: "480px" }} />
              </div>
            </div>
            <div>
              <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: C.teal }}>Gegründet & entwickelt von</p>
              <h2 className="font-black uppercase leading-none mb-5 text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 5vw, 3rem)" }}>Larissa Girenko</h2>
              <p className="text-gray-300 text-sm leading-relaxed mb-6">
                DSGVO-Verstöße können für viele Unternehmen existenzbedrohend sein – und die meisten Websites wissen nicht, wo sie stehen. Transparenz ist der erste Schritt zur Compliance.
              </p>
              <p className="text-gray-400 text-sm leading-relaxed mb-8">
                Basierend auf der DSGVO, dem TTDSG, dem Telemediengesetz und den aktuellen Datenschutzbehörden-Entscheidungen – das erste messbare System für Website-Datenschutz, das auch kleine Unternehmen ohne Anwalt nutzen können.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[{ value: "5", label: "Dimensionen" }, { value: "160+", label: "Kriterien geprüft" }, { value: "DSGVO", label: "Rechtsrahmen" }, { value: "TTDSG", label: "Cookie-Recht" }].map((stat) => (
                  <div key={stat.label} className="border-l-2 pl-4" style={{ borderColor: C.teal }}>
                    <p className="text-white font-bold text-xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{stat.value}</p>
                    <p className="text-gray-400 text-xs">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Selling */}
      <CrossSelling
        headline="Datenschutz gesichert – jetzt auch sichtbar werden"
        subline="Rechtliche Sicherheit ist die Pflicht – diese Barometer sichern deinen Marketingerfolg."
        items={[
          {
            icon: "/manus-storage/16_9c3f7718.svg",
            title: "Accessibility-Barometer™",
            description: "Barrierefreiheit ist Pflicht ab 2025. Prüfe jetzt deine Website auf WCAG-Konformität.",
            price: formatPrice(BAROMETER_PRICES.accessibility),
            href: "/accessibility-barometer",
            accent: "#0f766e",
            badge: "Ergänzt Datenschutz",
          },
          {
            icon: "/manus-storage/23_1ab7147e.svg",
            title: "SEO-Barometer™",
            description: "Datenschutz + SEO = vertrauenswürdige Website. 100+ SEO-Kriterien für mehr Sichtbarkeit.",
            price: formatPrice(BAROMETER_PRICES.seo),
            href: "/seo-barometer",
            accent: "#0a5c36",
          },
          {
            icon: "/manus-storage/11_094992a3.svg",
            title: "Sensorik-Barometer™",
            description: "Wie wirkt deine Website auf das Unterbewusstsein? Vertrauen beginnt beim ersten Eindruck.",
            price: formatPrice(BAROMETER_PRICES.sensorik),
            href: "/sensorik",
            accent: "#6d0000",
          },
        ]}
      />

      {/* Footer */}
      <footer className="border-t border-gray-200 px-6 py-8" style={{ backgroundColor: C.bgGray }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AgencyLogo withLogo size="sm" />
          </div>
          <div className="flex flex-wrap items-center gap-4 justify-center">
            {[{ href: "/", label: "Übersicht" }, { href: "/sensorik", label: "Sensorik" }, { href: "/lokal", label: "Lokal" }, { href: "/geo-barometer", label: "GEO" }, { href: "/seo-barometer", label: "SEO" }, { href: "/conversion-barometer", label: "Conversion" }, { href: "/accessibility-barometer", label: "Accessibility" }, { href: "/datenschutz-barometer", label: "Datenschutz" }, { href: "/impressum", label: "Impressum" }].map(({ href, label }) => (
              <a key={href} href={href} className="text-xs text-gray-400 hover:text-gray-700 tracking-widest uppercase">{label}</a>
            ))}
          </div>
          <p className="text-xs text-gray-400">© 2026 Werbeagentur Larissa Girenko</p>
        </div>
      </footer>
    </div>
  );
}
